/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import { SOUNDING_COLORS } from '../Soundings/drawUtils';
import type { ScalesType } from '../Soundings/types';

interface InitializeSvg {
  svgRef: React.RefObject<SVGSVGElement | null>;
  widthHeight: number;
}

export const initializeSvg = ({
  svgRef,
  widthHeight,
}: InitializeSvg): d3.Selection<
  SVGSVGElement | null,
  unknown,
  null,
  undefined
> => {
  const svg = d3
    .select(svgRef.current)
    .attr('data-testid', 'hodograph')
    .attr('width', '100%')
    .attr('height', '100%')
    .attr('viewBox', `0 0 ${widthHeight + 3} ${widthHeight + 3}`)
    .attr('style', 'width: 100%; height: auto;');

  const gSel = svg.select('g.hodograph');
  if (gSel.empty()) {
    svg.append('g').attr('class', 'hodograph');
  }

  return svg;
};

export const createScales = (widthHeight: number): [ScalesType, ScalesType] => {
  const scaleRadius = d3
    .scaleLinear()
    .domain([0, 100])
    .range([0, widthHeight / 2]);

  const scaleAngle = d3
    .scaleLinear()
    .domain([0, 360])
    .range([0, 2 * Math.PI]);

  return [scaleRadius, scaleAngle];
};

interface DrawAxis {
  svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, undefined>;
  scaleRadius: ScalesType;
  scaleAngle: ScalesType;
  widthHeight: number;
}

export const drawAxes = ({
  svg,
  scaleRadius,
  scaleAngle,
  widthHeight,
}: DrawAxis): void => {
  // Draw circles
  svg
    .select('g')
    .selectAll('circle')
    .data([10, 20, 30, 40, 50, 60, 70, 80, 90, 100])
    .join((enter) =>
      enter
        .append('circle')
        .attr('cx', widthHeight / 2)
        .attr('cy', widthHeight / 2)
        .attr('fill', 'none')
        .attr('stroke', 'gray')
        .style('opacity', 0.6)
        .attr('stroke-dasharray', (data) =>
          data === 50 || data === 100 ? 'none' : '5, 4',
        )
        .attr('r', (data) => scaleRadius(data)),
    );

  // Draw windspeed scale
  const speedTicks = scaleRadius.ticks(9);
  svg
    .select('g')
    .append('g')
    .attr('type', 'windspeed')
    .attr('transform', `translate(${widthHeight / 2}, ${widthHeight / 2})`) // origin transformed to center
    .call(
      d3
        .axisBottom(scaleRadius)
        .tickValues(speedTicks)
        .tickSizeInner(0)
        .tickSizeOuter(0),
    )
    // Draw unit label
    .append('text')
    .attr('fill', 'currentColor')
    .attr('x', scaleRadius(90))
    .attr('y', '0')
    .attr('font-size', '11px')
    .attr('dy', '2.2em')
    .attr('dx', '0.8em')
    .attr('text-anchor', 'left')
    .text('kts');
};

// Use this function to filter the pressure and parameter array so no NaN or null values are included
export const filterDomainRangeForHodograph = (
  pressureDomain: number[],
  parameterDomain: [number, number][],
): [number[], [number, number][]] => {
  const filteredPressureDomain = pressureDomain.filter(
    (_value, index) =>
      parameterDomain[index] !== null &&
      !isNaN(parameterDomain[index][0]) &&
      parameterDomain[index][0] !== null &&
      !isNaN(parameterDomain[index][1]) &&
      parameterDomain[index][1] !== null,
  );
  const filteredParameterDomain = parameterDomain.filter(
    (value) =>
      !isNaN(value[0]) &&
      value[0] !== null &&
      !isNaN(value[1]) &&
      value[1] !== null,
  );

  // If first pressure value is smallest, reverse arrays to ensure we draw starting from surface
  if (
    filteredPressureDomain[0] <
    filteredPressureDomain[filteredPressureDomain.length - 1]
  ) {
    return [
      filteredPressureDomain.reverse(),
      filteredParameterDomain.reverse(),
    ];
  }
  return [filteredPressureDomain, filteredParameterDomain];
};

type PathSegments = [
  [number, number][],
  [number, number][],
  [number, number][],
  [number, number][],
  [number, number][],
];

const T0_SEA = 288.15; // Standard temperature in Kelvin (15 degrees Celsius)
const P0_SEA = 101325; // Reference pressure (Pa)

const R_GAS = 8.314462618; // Universal gas constant (J/mol K)
const L_TEMP = 0.0065; // Temperature gradient (K/m) at sea level
const G_ACC = 9.80665; // The gravitational acceleration in units of geopotential height (m/s2)
const M_AIR = 0.0289647; // Mean molar mass of air at sea level (kg/mol)
const exponent = (R_GAS * L_TEMP) / (G_ACC * M_AIR); // ≈ 0.1903

// This function returns the altitude above MSL using the barometric formula in m
// Pressure passed in is in hPa, which is converted to Pa (pressure * 100)
export const altitudeBarometricFromPressure = (pressure: number): number => {
  const altitude =
    (T0_SEA / L_TEMP) * ((P0_SEA / (pressure * 100)) ** exponent - 1);
  return altitude;
};

export const generateSegmentsBasedOnLevel = (
  pressure: number[],
  windValues: [number, number][],
): PathSegments => {
  const segments: PathSegments = [[], [], [], [], []];
  pressure.forEach((pressureVal, index): void => {
    // Ensure we ignore the last value
    if (pressure[index + 1]) {
      const heightInMeters = altitudeBarometricFromPressure(pressureVal);

      // index 0, red line
      if (heightInMeters < 1000) {
        segments[0].push(windValues[index]);
        segments[0].push(windValues[index + 1]);
      }
      // index 1, green line
      if (heightInMeters >= 1000 && heightInMeters < 3000) {
        segments[1].push(windValues[index]);
        segments[1].push(windValues[index + 1]);
      }
      // index 2, yellow line
      if (heightInMeters >= 3000 && heightInMeters < 6000) {
        segments[2].push(windValues[index]);
        segments[2].push(windValues[index + 1]);
      }
      // index 3, purple line
      if (heightInMeters >= 6000 && heightInMeters < 9000) {
        segments[3].push(windValues[index]);
        segments[3].push(windValues[index + 1]);
      }
      // index 4, black line
      if (heightInMeters >= 9000 && heightInMeters <= 19000) {
        segments[4].push(windValues[index]);
        segments[4].push(windValues[index + 1]);
      }
    }
  });
  return segments;
};

export const HODOGRAPH_COLORS = [
  'red',
  'olivedrab',
  'orange',
  'darkviolet',
  'black',
];

export const getColorForSegment = (index: number): string => {
  if (HODOGRAPH_COLORS[index]) {
    return HODOGRAPH_COLORS[index];
  }
  return 'black';
};

interface PlotHodograph {
  windValues: [number, number][];
  pressureValues: number[];
  scaleAngle: ScalesType;
  scaleRadius: ScalesType;
  widthHeight: number;
  svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, undefined>;
  index: number;
}

export const plotHodograph = ({
  windValues,
  pressureValues,
  scaleAngle,
  scaleRadius,
  widthHeight,
  svg,
  index,
}: PlotHodograph): void => {
  // Filter out any NaN or null values
  const [filteredPressureValues, filteredWindValues] =
    filterDomainRangeForHodograph(pressureValues, windValues);

  // Construct path segments to be able to give each segment it's own color
  const pathSegments = generateSegmentsBasedOnLevel(
    filteredPressureValues,
    filteredWindValues,
  );

  // index 0 = wind direction, index 1 = wind speed
  const lineGenerator = d3
    .lineRadial()
    .angle((dataPoint) => scaleAngle(dataPoint[0]))
    .radius((dataPoint) => scaleRadius(dataPoint[1]));

  svg
    .select('g.hodograph')
    .selectAll(`path.dataline-${index}`)
    .data(pathSegments)
    .enter()
    .append('path')
    .classed(`dataline-${index}`, true)
    .attr('data-testid', `hodograph-dataline-${index}`)
    .attr('transform', `translate(${widthHeight / 2}, ${widthHeight / 2})`)
    .attr('fill', 'none')
    .attr('stroke', (data, segmentIndex) => getColorForSegment(segmentIndex))
    .attr('stroke-width', 2)
    .style('stroke-opacity', 1)
    .attr('d', (data) => lineGenerator(data));

  // Adding a dot for the very first data point in the color used in the soundings graph
  const firstDataPoint = filteredWindValues[0];
  if (firstDataPoint) {
    const color = SOUNDING_COLORS[index] || SOUNDING_COLORS[0];
    svg
      .select('g.hodograph')
      .append('g')
      .attr('transform', `translate(${widthHeight / 2}, ${widthHeight / 2})`) // origin transformed to center
      .attr('fill', color)
      .attr('stroke', 'none')
      .attr('data-testid', `hodograph-datastart-${index}`)
      .selectAll()
      .data([firstDataPoint])
      .join('circle')
      .attr('cx', (dataPoint) => {
        const angle = scaleAngle(dataPoint[0]);
        const radius = scaleRadius(dataPoint[1]);
        return radius * Math.sin(angle);
      })
      .attr('cy', (dataPoint) => {
        const angle = scaleAngle(dataPoint[0]);
        const radius = scaleRadius(dataPoint[1]);
        return radius * -1 * Math.cos(angle);
      })
      .attr('r', 3);
  }
};
